import React, { useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

function EmailImportBox({ setPendingContent, onScrub, setUploadedFileName, setUploadedFile, resetResults, userCredits, onModeChangeReset}) {
    const [mode, setMode] = useState("file");
    const [manualText, setManualText] = useState("");
    const [fileName, setFileName] = useState("");
    const [fileError, setFileError] = useState("");
    const MAX_FILE_SIZE_MB = 5;
    const [emailCount, setEmailCount] = useState(null);
    const [user] = useAuthState(auth);
    const navigate = useNavigate();

    const handleFileUpload = (event) => {
        const file = event.target.files[0];

        if (!file) return;

        const isValid = file.name.endsWith(".csv") || file.name.endsWith(".txt") ||
            file.name.endsWith(".xlsx") || file.name.endsWith(".xls");

        if (!isValid) {
            setFileError("❌ Only .csv, .txt, or .xlsx files are supported.");
            setFileName("");
            return;
        }

        setFileError("");
        setFileName(file.name);
        setUploadedFileName(file.name);
        setUploadedFile(file);

        const isTooLarge = file.size > MAX_FILE_SIZE_MB * 1024 * 1024;
        if (isTooLarge) {
            setFileError(`❌ File is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`);
            setFileName("");
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            const content = e.target.result;

            if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
                const workbook = XLSX.read(content, { type: "binary" });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                const flat = jsonData.flat();
                const emails = flat
                    .map(e => (typeof e === "string" ? e.trim() : ""))
                    //.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

                setPendingContent(emails.join("\n"));
                setEmailCount(emails.length);
            } else {
                // .csv or .txt
                const text = content;
                setPendingContent(text);

                const emails = text
                    .split(/[\n,]+/)
                    .map((e) => e.trim())
                    .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

                setEmailCount(emails.length);
            }
        };

        // Important: readAsBinaryString for Excel files
        if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
            reader.readAsBinaryString(file);
        } else {
            reader.readAsText(file);
        }
    };
    // @Deprecated
    // const handleManualSubmit = () => {
    //     const emails = manualText
    //         .split(/[\n,]+/)
    //         .map((e) => e.trim())
    //         .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    //
    //     setEmailCount(emails.length);
    //     setFileName("Manual input"); // For consistency
    //     setFileError(""); // Clear any past errors
    //
    //     setPendingContent(manualText);
    // };
    const handleModeChange = (newMode) => {
        setMode(newMode);
        setFileName("");
        setFileError("");
        setEmailCount(null);
        setManualText("");
        resetResults();
        onModeChangeReset();

    };

    const handleScrubOrRedirect = () => {
        if (!user) return navigate("/login");
        if (emailCount > userCredits) return navigate("/pricing");
        onScrub();
    };

    const emailsExceedCredits = emailCount !== null && userCredits !== null && emailCount > userCredits;

    const handleDrop = (e) => {
        e.preventDefault(); // 🔒 prevents file from opening in browser
        const file = e.dataTransfer.files[0];

        if (!file) return;

        const isValid = file.name.endsWith(".csv") || file.name.endsWith(".txt") || file.name.endsWith(".xlsx") || file.name.endsWith(".xls");

        if (!isValid) {
            setFileError("❌ Only .csv and .txt files are supported.");
            setFileName("");
            return;
        }

        setFileError("");
        setFileName(file.name);
        setUploadedFileName(file.name);
        setUploadedFile(file);

        const isTooLarge = file.size > MAX_FILE_SIZE_MB * 1024 * 1024;
        if (isTooLarge) {
            setFileError(`❌ File is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`);
            setFileName("");
            return;
        }

        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target.result;
                setPendingContent(content);

                // 🔍 Parse emails for preview
                const emails = content
                    .split(/[\n,]+/)
                    .map((e) => e.trim())
                    .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

                setEmailCount(emails.length);
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="import-box">
            <h3>Select a source</h3>

            <div className="import-tabs">
                <button
                    className={mode === "file" ? "active" : ""}
                    onClick={() => handleModeChange("file")}
                >
                    📁 My Computer
                </button>
                <button
                    className={mode === "manual" ? "active" : ""}
                    onClick={() => handleModeChange("manual")}
                >
                    📋 Copy & Paste
                </button>
            </div>

            <div className="drop-area-container">
            {mode === "file" ? (
                <div className="drop-area"
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={handleDrop}>
                    {user && (
                        <label className="custom-upload-label">
                            <input
                                type="file"
                                accept=".txt,.csv,.xls,.xlsx"
                                onChange={handleFileUpload}
                                className="hidden-file-input"
                            />
                            {fileName ? (
                                <span className="uploaded-filename">✅ {fileName}</span>
                            ) : (
                                <span>📁 Click here to upload your file</span>
                            )}
                        </label>
                    )}
                    {user ? (
                        !fileName && (
                            <p>Drag and drop to upload a file. Max file size is {MAX_FILE_SIZE_MB}MB.</p>
                        )
                    ) : (
                        <div className="drop-area-overlay">
                            <p><a href="/login">SIGN IN</a> to start scrubing your mails.</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="dashed-container">
                    {user ? (
                            <div className="textarea-wrapper">
                                <textarea
                                    placeholder="Enter emails, one per line or comma-separated"
                                    value={manualText}
                                    onChange={(e) => {
                                        const text = e.target.value;
                                        setManualText(text);
                                        setPendingContent(text); // Keep parent in sync
                                        const emails = text
                                            .split(/[\n,]+/)
                                            .map((e) => e.trim())
                                            .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

                                        setEmailCount(emails.length);
                                    }}
                                />
                            </div>
                        ) : (
                        <div className="drop-area-overlay">
                            <p><a href="/login">SIGN IN</a> to start scrubing your mails.</p>
                        </div>
                        // <p>Please <a href="/register">register now</a> and receive 300 FREE CREDITS.</p>
                    )}

                </div>
            )}
            </div>

            <button
                className="login-submit-btn"
                onClick={() => {
                    handleScrubOrRedirect()
                }}
            >
                {emailsExceedCredits ? "TOP UP CREDITS" : (user ? "SCRUB MAILS" : "SIGN IN")}
            </button>
            {fileError && (
                <p style={{ color: "red", marginTop: "8px", fontSize: "1rem", fontWeight: "600" }}>
                    {fileError}
                </p>
            )}
            {fileName && (
                <p style={{ marginTop: "10px",fontSize: "1.2rem", fontWeight: "600", color: "#555" }}>
                    ✅ File uploaded: <strong>{fileName}</strong>
                </p>
            )}
            {/*{fileName && (*/}
            {/*    <p style={{ marginTop: "10px", fontSize: "1.2rem", fontWeight: "600", color: "#555" }}>*/}
            {/*        ✅*/}
            {/*        /!*Source: <strong>{fileName}</strong>*!/*/}
            {/*        {emailCount !== null && (*/}
            {/*            <span> Scan done : <strong>{emailCount}</strong> emails found (<strong>max. {emailCount} Credits</strong>)</span>*/}
            {/*        )}*/}
            {/*    </p>*/}
            {/*)}*/}
            {emailCount !== null && (
                <p style={{ marginTop: "10px", fontSize: "1.2rem", fontWeight: "600", color: emailsExceedCredits ? "red" : "#555" }}>
                    {emailsExceedCredits ? (
                        <>❌ You uploaded <strong>{emailCount}</strong> emails but only have <strong>{userCredits}</strong> credits.</>
                    ) : (
                        <>✅ {emailCount} emails detected. (<strong>max. {emailCount} credits</strong>)</>
                    )}
                </p>
            )}
        </div>
    );
}

export default EmailImportBox;